import type { BestFitPlane } from './pointInPolygon';
import type { MaterialId } from './materials';

/**
 * Flat shape of a saved stockpile feature's `properties` JSONB column.
 *
 * Order matters here — the AttributeTable, FeatureProperties drawer, and
 * exported GeoJSON all surface property keys in insertion order. The user-
 * visible columns therefore start with name, then headline metrics
 * (volume, mass, area, heights), then material, then sampling parameters,
 * then base-plane coefficients, then provenance, with the `kind` tag last.
 *
 * Earlier the properties used nested objects (`result`, `mass`, `material`,
 * `basePlane`) — that rendered as the literal `[object Object]` in any
 * generic property surface, made the GeoJSON export awkward, and pushed
 * the headline volume below housekeeping fields. The shape below fixes
 * all three at once. `parseStockpileProperties` keeps reading the old
 * shape for already-saved stockpiles in the DB.
 */
export interface StockpileFeatureProperties {
  /** User-given name. Empty string if unset (server stores untitled label client-side). */
  name: string;

  // ── Headline metrics ──────────────────────────────────────────────────
  volumeM3: number;
  /** null when no material was selected — mass requires a density. */
  massKg: number | null;
  areaM2: number;
  meanHeightM: number;
  maxHeightM: number;

  // ── Material ──────────────────────────────────────────────────────────
  /** `'none'` if no material picked; otherwise the material id. */
  materialId: MaterialId;
  /** kg/m³. null when materialId === 'none'. */
  densityKgPerM3: number | null;

  // ── Sampling parameters ───────────────────────────────────────────────
  cellSize: number;
  samplingQuality: 'standard' | 'fine';
  /** Inside-polygon cells that received at least one point. */
  sampleCount: number;
  /** Inside-polygon cells with zero points — coverage holes. */
  missCount: number;

  // ── Base plane (flattened from BestFitPlane) ─────────────────────────
  // Used by FeatureProperties' "base elevation at centroid" readout via
  // `evalBestFitPlane`. Flat fields keep the GeoJSON export readable;
  // `getStockpileBasePlane` rebuilds the BestFitPlane object on read.
  basePlaneA: number;
  basePlaneB: number;
  basePlaneC0: number;
  basePlaneX0: number;
  basePlaneY0: number;
  basePlaneZ0: number;

  // ── Provenance ───────────────────────────────────────────────────────
  /** PCO layer id the polygon was originally sampled against. Required
   *  for re-entering edit mode (the StockpileTool seeds its source layer
   *  from this). */
  sourceLayerId: string;
  computedAt: number;

  // ── Tag ───────────────────────────────────────────────────────────────
  /** Discriminator. Always `'STOCKPILE'` — used by everything that filters
   *  the feature stream to "just stockpiles". Last in insertion order so
   *  it doesn't push real metrics down in generic property surfaces. */
  kind: 'STOCKPILE';
}

/**
 * Parse a feature's raw `properties` blob into the flat shape, accepting
 * both the new flat layout and the older nested layout (`result.*`,
 * `mass.*`, `material.*`, `basePlane.*`) that stockpiles saved before
 * this refactor. Keeps existing DB rows readable without a migration.
 *
 * Missing fields fall back to safe defaults — readers should still gate
 * UI on `samplesAvailable` when zero-result is misleading.
 */
export function parseStockpileProperties(raw: unknown): StockpileFeatureProperties | null {
  if (raw == null || typeof raw !== 'object') return null;
  const p = raw as Record<string, any>;
  if (p.kind !== 'STOCKPILE') return null;

  // Helpers — pick a flat-or-nested value, preferring the flat new shape.
  const num = (flat: unknown, nested: unknown, fallback = 0): number =>
    typeof flat === 'number' && Number.isFinite(flat)
      ? flat
      : typeof nested === 'number' && Number.isFinite(nested)
        ? nested
        : fallback;
  const numOrNull = (flat: unknown, nested: unknown): number | null =>
    typeof flat === 'number' && Number.isFinite(flat)
      ? flat
      : typeof nested === 'number' && Number.isFinite(nested)
        ? nested
        : null;

  const r = (p.result ?? {}) as Record<string, any>;
  const m = (p.mass ?? {}) as Record<string, any>;
  const mat = (p.material ?? {}) as Record<string, any>;
  const bp = (p.basePlane ?? {}) as Record<string, any>;

  const materialId: MaterialId =
    (typeof p.materialId === 'string' && p.materialId
      ? p.materialId
      : typeof mat.id === 'string'
        ? mat.id
        : 'none') as MaterialId;

  return {
    name: typeof p.name === 'string' ? p.name : '',
    volumeM3: num(p.volumeM3, r.volumeM3),
    massKg: numOrNull(p.massKg, m.kg),
    areaM2: num(p.areaM2, r.areaM2),
    meanHeightM: num(p.meanHeightM, r.meanHeightM),
    maxHeightM: num(p.maxHeightM, r.maxHeightM),
    materialId,
    densityKgPerM3: numOrNull(p.densityKgPerM3, mat.densityKgPerM3),
    cellSize: num(p.cellSize, undefined, 0.25),
    samplingQuality: p.samplingQuality === 'fine' ? 'fine' : 'standard',
    sampleCount: num(p.sampleCount, r.sampleCount),
    missCount: num(p.missCount, r.missCount),
    basePlaneA: num(p.basePlaneA, bp.a),
    basePlaneB: num(p.basePlaneB, bp.b),
    basePlaneC0: num(p.basePlaneC0, bp.c0),
    basePlaneX0: num(p.basePlaneX0, bp.x0),
    basePlaneY0: num(p.basePlaneY0, bp.y0),
    basePlaneZ0: num(p.basePlaneZ0, bp.z0),
    sourceLayerId: typeof p.sourceLayerId === 'string' ? p.sourceLayerId : '',
    computedAt: num(p.computedAt, r.computedAt),
    kind: 'STOCKPILE',
  };
}

/** Reassemble a `BestFitPlane` from the flat properties — for `evalBestFitPlane`. */
export function getStockpileBasePlane(p: StockpileFeatureProperties): BestFitPlane {
  return {
    a: p.basePlaneA,
    b: p.basePlaneB,
    c0: p.basePlaneC0,
    x0: p.basePlaneX0,
    y0: p.basePlaneY0,
    z0: p.basePlaneZ0,
  };
}

/**
 * Round to 4 decimal places (sub-millimetre precision for metric stockpile
 * measurements). Trims the JSON written to the DB / GeoJSON export from
 * numbers like `12.345678901234` to `12.3457`, which looks dramatically
 * better in the attribute table and GeoJSON viewer without losing
 * engineering-relevant precision. Non-finite inputs pass through
 * unchanged.
 */
const round4 = (v: number): number =>
  Number.isFinite(v) ? Math.round(v * 10000) / 10000 : v;

const round4OrNull = (v: number | null): number | null =>
  v == null ? null : round4(v);

/**
 * Build the flat properties object that gets written to the Feature row.
 * Insertion order matches the `StockpileFeatureProperties` interface
 * declaration order — see the docstring there for the rationale.
 *
 * Numeric fields are rounded to 4 decimals (`round4`) so the persisted
 * JSON / GeoJSON / attribute-table renderings stay readable. `cellSize`
 * is preserved at full precision because it's a user-entered parameter,
 * and `sampleCount` / `missCount` / `computedAt` are integers.
 */
export function buildStockpileProperties(input: {
  name: string;
  volumeM3: number;
  massKg: number | null;
  areaM2: number;
  meanHeightM: number;
  maxHeightM: number;
  materialId: MaterialId;
  densityKgPerM3: number | null;
  cellSize: number;
  samplingQuality: 'standard' | 'fine';
  sampleCount: number;
  missCount: number;
  basePlane: BestFitPlane;
  sourceLayerId: string;
  computedAt: number;
}): StockpileFeatureProperties {
  return {
    name: input.name,
    volumeM3: round4(input.volumeM3),
    massKg: round4OrNull(input.massKg),
    areaM2: round4(input.areaM2),
    meanHeightM: round4(input.meanHeightM),
    maxHeightM: round4(input.maxHeightM),
    materialId: input.materialId,
    densityKgPerM3: round4OrNull(input.densityKgPerM3),
    cellSize: input.cellSize,
    samplingQuality: input.samplingQuality,
    sampleCount: input.sampleCount,
    missCount: input.missCount,
    basePlaneA: round4(input.basePlane.a),
    basePlaneB: round4(input.basePlane.b),
    basePlaneC0: round4(input.basePlane.c0),
    basePlaneX0: round4(input.basePlane.x0),
    basePlaneY0: round4(input.basePlane.y0),
    basePlaneZ0: round4(input.basePlane.z0),
    sourceLayerId: input.sourceLayerId,
    computedAt: input.computedAt,
    kind: 'STOCKPILE',
  };
}
